import React from "react";
import { Link } from "react-router-dom";

// This is the component for the SelectProposal blog detail page
const ThirdBlog = () => {
  // This would typically be fetched based on URL params in a real app
  const article = {
    id: 1,
    title: "How AI is Transforming Proposal Development",
    subtitle: "Leveraging technology for better business outcomes",
    category: "BUSINESS INNOVATION",
    publishDate: "April 24, 2025",
    author: {
      name: "Sarah Johnson",
      title: "Chief Innovation Officer",
      image: "https://randomuser.me/api/portraits/women/42.jpg",
    },
    featuredImage:
      "https://plus.unsplash.com/premium_photo-1663076211121-36754a46de8d?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8bWFycmlhZ2V8ZW58MHx8MHx8fDA%3D",
    readTime: "7 min read",
    content: `
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">The Evolution of Proposal Development</h2>
      <p>In today's competitive business landscape, the ability to create compelling, accurate, and personalized business proposals quickly can make the difference between winning and losing valuable contracts. Traditional proposal development processes have often been time-consuming, involving multiple revisions, extensive research, and significant manual effort.</p>
      
      <p>However, with the advent of artificial intelligence, the proposal development landscape is undergoing a revolutionary transformation. AI-powered tools like SelectProposal are changing how businesses approach proposals, offering unprecedented efficiency and effectiveness.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Key AI Innovations in Proposal Development</h2>
      <p>SelectProposal's platform leverages several cutting-edge AI technologies to streamline and enhance the proposal creation process:</p>
      
      <ul>
        <li><strong>Natural Language Processing (NLP):</strong> Analyzes RFPs and client requirements to extract key points and compliance needs</li>
        <li><strong>Machine Learning:</strong> Identifies patterns in successful proposals to recommend high-impact content</li>
        <li><strong>Predictive Analytics:</strong> Forecasts win probabilities based on proposal content and historical data</li>
        <li><strong>Content Generation:</strong> Creates tailored sections, executive summaries, and boilerplate text</li>
        <li><strong>Data Visualization:</strong> Automatically generates charts and graphs from client data</li>
      </ul>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Transforming Efficiency and Results</h2>
      <p>The implementation of AI in proposal development has delivered remarkable results for businesses across industries. Our research indicates that companies using AI-powered proposal tools experience:</p>
      
      <ul>
        <li>70% reduction in proposal development time</li>
        <li>42% improvement in proposal win rates</li>
        <li>85% decrease in compliance errors</li>
        <li>63% enhancement in client-specific customization</li>
        <li>58% increase in team collaboration efficiency</li>
      </ul>
      
      <p>These efficiency gains translate directly to bottom-line results, allowing businesses to pursue more opportunities without increasing headcount or sacrificing quality.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">The Human-AI Partnership</h2>
      <p>Despite the advanced capabilities of AI in proposal development, the technology works best as a complement to human expertise rather than a replacement. The most successful implementations of AI proposal tools maintain the critical balance between technological efficiency and human creativity.</p>
      
      <p>At SelectProposal, we've designed our platform to enhance human capabilities by handling routine tasks, providing data-driven insights, and offering suggestions—all while keeping the proposal team in control of strategy, messaging, and final decisions.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Overcoming Implementation Challenges</h2>
      <p>While the benefits of AI-powered proposal development are significant, organizations may face challenges during implementation:</p>
      
      <ul>
        <li>Resistance to new technologies and workflows</li>
        <li>Integration with existing CRM and document management systems</li>
        <li>Building and maintaining quality data for AI training</li>
        <li>Ensuring compliance with industry regulations and data privacy requirements</li>
      </ul>
      
      <p>SelectProposal addresses these challenges through our comprehensive onboarding process, flexible API integrations, pre-trained industry-specific models, and robust security infrastructure.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Looking Ahead: The Future of AI in Proposals</h2>
      <p>As AI technology continues to advance, we anticipate several exciting developments in proposal development:</p>
      
      <ul>
        <li>Real-time collaboration with AI co-writers</li>
        <li>Adaptive proposals that dynamically adjust to reader behavior</li>
        <li>Advanced sentiment analysis to optimize persuasive language</li>
        <li>Automated competitive analysis and differentiation recommendations</li>
        <li>Integration with augmented reality for interactive proposal experiences</li>
      </ul>
      
      <p>These innovations will further enhance the strategic advantage that AI-powered proposals provide to forward-thinking organizations.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Conclusion: The Competitive Advantage</h2>
      <p>In today's fast-paced business environment, organizations that leverage AI for proposal development gain a significant competitive edge. By reducing the time and resources required for proposal creation while simultaneously improving quality and customization, AI tools like SelectProposal enable businesses to respond to more opportunities and win more contracts.</p>
      
      <p>The question is no longer whether AI has a place in proposal development, but rather how quickly organizations will adopt and master these powerful new tools.</p>
    `,
    tags: [
      "AI Technology",
      "Business Proposals",
      "Digital Transformation",
      "SelectProposal",
      "Business Efficiency",
    ],
    relatedPosts: [
      {
        id: 2,
        title: "Master the Art of Proposal Writing",
        image:
          "https://images.unsplash.com/photo-1527842891421-42eec6e703ea?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MjB8fG5pZ2h0JTIwc2t5JTIwbW9vbnxlbnwwfHwwfHx8MA%3D%3D",
        category: "BUSINESS SKILLS",
      },
      {
        id: 3,
        title: "Client Success Story: From Startup to Industry Leader",
        image:
          "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTcZdengGsiEcXWajdsUEoct_8eocP4xwwS9Q&s",
        category: "CASE STUDY",
      },
      {
        id: 4,
        title: "5 Common Proposal Mistakes That Cost You Clients",
        image:
          "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80",
        category: "BUSINESS STRATEGY",
      },
    ],
  };

  return (
    <div className="font-sans max-w-4xl mx-auto p-4 pb-16">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-pink-500">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/blogs" className="hover:text-pink-500">
          Blogs
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-700">
          {article.title.substring(0, 30)}...
        </span>
      </div>

      {/* Category Tag */}
      <div className="mb-4 text-pink-500 font-bold">{article.category}</div>

      {/* Article Title */}
      <h1 className="text-4xl font-serif font-bold mb-2">{article.title}</h1>
      <h2 className="text-2xl font-serif text-gray-600 mb-6">
        {article.subtitle}
      </h2>

      {/* Article Meta */}
      <div className="flex items-center justify-between mb-8 border-b border-gray-200 pb-4">
        <div className="flex items-center">
          <img
            src={article.author.image}
            alt={article.author.name}
            className="w-12 h-12 rounded-full mr-4 hover:scale-105 transition-transform duration-300"
          />
          <div>
            <div className="font-medium">{article.author.name}</div>
            <div className="text-sm text-gray-500">{article.author.title}</div>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          <span>{article.publishDate}</span>
          <span className="mx-2">•</span>
          <span>{article.readTime}</span>
        </div>
      </div>

      {/* Featured Image with Label */}
      <div className="mb-8 relative">
        <img
          src={article.featuredImage}
          alt={article.title}
          className="w-full h-auto rounded-lg shadow-md hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-4 right-4 bg-pink-500 text-white p-3 text-sm font-bold">
          <div>Business</div>
          <div>Innovation</div>
        </div>
      </div>

      {/* Article Content */}
      <div
        className="prose prose-lg max-w-none mb-12"
        dangerouslySetInnerHTML={{ __html: article.content }}
      />

      {/* Tags */}
      <div className="mb-12">
        <h3 className="text-lg font-serif font-bold mb-4 text-pink-500">
          Related Topics
        </h3>
        <div className="flex flex-wrap gap-2">
          {article.tags.map((tag, index) => (
            <span
              key={index}
              className="bg-gray-100 cursor-default hover:bg-gray-200 text-gray-500 px-3 py-1 rounded-full text-sm"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Author Box */}
      <div className="bg-gray-50 p-6 rounded-lg shadow-sm mb-12">
        <div className="flex items-start">
          <img
            src={article.author.image}
            alt={article.author.name}
            className="w-16 h-16 rounded-full mr-6 hover:scale-105 transition-transform duration-300"
          />
          <div>
            <h3 className="font-bold text-lg font-serif mb-2">
              About {article.author.name}
            </h3>
            <p className="text-gray-700 mb-4">
              Sarah Johnson is a pioneering expert in business technology with
              over 15 years of experience in digital transformation. As Chief
              Innovation Officer at SelectProposal, she leads product
              development and strategic initiatives focused on integrating
              cutting-edge AI solutions into business processes. Sarah regularly
              speaks at industry conferences and has published numerous articles
              on the intersection of technology and business development.
            </p>
            <div className="flex gap-4">
              <span className="text-pink-500 cursor-default hover:text-pink-500">
                More articles by this author
              </span>
              <span className="text-pink-500 cursor-default hover:text-pink-500">
                Follow on LinkedIn
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Related Posts */}
      <div>
        <h3 className="text-2xl font-serif font-bold mb-6 cursor-default text-pink-500">
          Related Articles
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {article.relatedPosts.map((post) => (
            <span
              key={post.id}
              className="border-b-4 border-red-600 rounded-bl-md rounded-br-md"
            >
              <div className="mb-3">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-48 object-cover rounded-lg hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="mb-1 text-pink-500 font-bold text-sm cursor-default">
                {post.category}
              </div>
              <h4 className="text-lg font-serif font-bold hover:text-[#730000] cursor-default">
                {post.title}
              </h4>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

// This is the component displaying the card grid that links to article details
const SelectProposalArticleCards = () => {
  const articles = [
    {
      id: 1,
      title: "How AI is Transforming Proposal Development",
      excerpt:
        "Discover how SelectProposal's AI-powered tools are helping businesses create winning proposals in less time with greater accuracy...",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTrwUa5tDPo-5UloVBc5X7ile4aoQTHh0W6lw&s",
      badge: {
        top: "Featured",
        bottom: "Article",
      },
      bottomLabel: "Business Innovation",
    },
    {
      id: 2,
      title: "Master the Art of Proposal Writing",
      excerpt:
        "Learn the key strategies to craft compelling business proposals that stand out from the competition and win more clients.",
      image:
        "https://images.unsplash.com/photo-1527842891421-42eec6e703ea?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MjB8fG5pZ2h0JTIwc2t5JTIwbW9vbnxlbnwwfHwwfHx8MA%3D%3D",
    },
    {
      id: 3,
      title: "Client Success Story: From Startup to Industry Leader",
      excerpt:
        "See how one of our clients used SelectProposal to secure major contracts and grow their business exponentially in just 18 months.",
      image:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTcZdengGsiEcXWajdsUEoct_8eocP4xwwS9Q&s",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {articles.map((article) => (
        <Link
          key={article.id}
          to={`/articles/${article.id}`}
          className="relative block hover:opacity-95 transition-opacity"
        >
          <div className="relative">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-48 object-cover"
            />
            {article.badge && (
              <div className="absolute top-0 right-0 bg-pink-500 text-white p-2 text-sm">
                <div>{article.badge.top}</div>
                <div>{article.badge.bottom}</div>
              </div>
            )}
            {article.bottomLabel && (
              <div className="absolute bottom-8 left-0 bg-white bg-opacity-75 p-1">
                <p className="text-sm font-bold">{article.bottomLabel}</p>
              </div>
            )}
            <h3 className="font-serif font-bold mt-3">{article.title}</h3>
            <p className="text-xs mt-1">{article.excerpt}</p>
          </div>
        </Link>
      ))}
    </div>
  );
};

export { ThirdBlog, SelectProposalArticleCards };
